/**
 * Panel "Terminänderungen" der Übersicht - Auftrag F. Neue Fläche OHNE Bestandsvorbild (`index.html`
 * kennt DSV-Terminabgleich nicht): kein Wortlautvergleich, eigenständiger Verhaltenstest, wie
 * `Artikelbild.tsx`/`LieferantenSeite.tsx` es für ihre jeweils neuen Flächen schon vormachen.
 *
 * ================= Was der Auftraggeber sehen will (wörtlich) =================
 *
 * "anzeigen können welche Termine sich verändert haben, und er soll bestätigen können, dass er die
 * Info zur Kenntnis genommen hat. Darstellung alter Termin (was für ein Termin - eta, etd, etc.), was
 * ist der neue Termin, geclustert je Sendung, und einen Absprungpunkt zur zugehörigen Bestellung."
 *
 * ================= Wiederverwendete Gestaltungssprache (`Handlungsbedarf.tsx`, Vorbild) ==========
 *
 * `ops-panel`/`ops-panel-head`/`ops-panel-title`/`ops-count` für den Rahmen und die Zahl im Titel,
 * `ops-actions` für die Liste, `ops-arrow` für den Absprung-Pfeil je Bestellung, `ops-empty` für den
 * Leerzustand, `ops-panel-more` für dieselbe 6/Infinity-Aufklappmechanik wie im Handlungsbedarf. Die
 * gruppen-/zeilenspezifischen Klassen (`ops-termine-*`) sind neu, weil es dafür kein Gegenstück im
 * Handlungsbedarf gibt (der ist eine flache Liste, keine Gruppe-mit-Unterzeilen) - siehe
 * `styles/neubau/terminaenderungen.css`.
 *
 * ================= Selbstständiger Abruf (Muster: `Artikelbild.tsx`) =================
 *
 * Die Komponente lädt ihre Daten selbst (`GET /api/dsv/terminaenderungen`, nur unbestätigte
 * Änderungen) und hält sie in lokalem State - nicht als Prop von `UebersichtSeite`/`App.tsx`, weil es
 * dafür (anders als bei `bestellungen`/`lieferanten`) noch keine Anbindung an den globalen Store gibt
 * (Auftrag E, Backend, läuft parallel; eine Store-Anbindung ist nicht Teil dieses Auftrags).
 *
 * Ein Fehlschlag beim Laden ist SICHTBAR (Fehleranzeige Paket 2, Task 8, Regel L1): im Kasten steht statt des
 * Leertexts „Die Terminänderungen konnten nicht geladen werden." samt Ursache und „Erneut laden"
 * (`Ladefehler/Ladefehler.tsx`), der Zähler im Titel entfällt (ein „0" behauptete, es gebe nichts), und es
 * entsteht kein Eintrag im Fehlerstapel (kein Doppel). „Keine offenen Terminänderungen." heisst nur noch:
 * geladen, und es gab nichts. Eine Antwort ohne `sendungen` zählt ebenfalls als Ladefehler; der frühere
 * Rückfall auf eine leere Liste ist entfallen. Abbruch (Unmount) und 401 erzeugen keine Fläche.
 * Beim Bestätigen (siehe unten) bleibt der Fehlschlag ebenfalls ausdrücklich sichtbar
 * (Auftragstext: "nie stillschweigend ausblenden").
 *
 * Bestätigen entfernt die Gruppe NUR LOKAL aus der Liste (kein Neuladen der ganzen Seite, siehe
 * Auftragstext) - scheitert der Aufruf, bleibt die Gruppe stehen und `gruppenFehler` trägt eine
 * Meldung für genau diese Sendung.
 *
 * ================= `istAktiveSeite`: geladen wird erst, wenn die Übersicht wirklich sichtbar ist ====
 *
 * `UebersichtSeite` ist - wie das Board - eine der ZWEI Seiten, die `App.tsx` IMMER zeichnet, auch
 * wenn eine andere Seite aktiv ist (siehe deren Kopfkommentar). Ein bedingungsloser Abruf beim
 * Mounten liefe deshalb bei JEDEM App-Rendern, unabhängig davon, ob der Nutzer die Übersicht je
 * ansieht - gemessen an einer echten Kollision: mehrere App-Verdrahtungstests mocken `fetch`
 * GLOBAL für ihre eigene Naht (`/api/auth/me`, `/api/jtl/warehouses`, …) und antworten auf JEDEN
 * unbekannten Pfad mit ihrer eigenen Antwortform - ein ungefragter Aufruf dieser Komponente traf
 * dort eine `{ user, permissions }`-Antwort ohne `sendungen`, setzte `sendungen` still auf
 * `undefined` und liess die Komponente beim naechsten Render abstuerzen (`sendungen.slice`). Ein
 * zweiter Test (`verdrahtungAnlage.test.tsx`) wirft dort sogar ausdruecklich, wenn ein Aufruf
 * ausserhalb der von IHM erwarteten Liste ankommt.
 *
 * Der Abruf ist deshalb an `istAktiveSeite` gebunden (Default `true` - ein direkt gerenderter Test
 * dieser Komponente oder von `UebersichtSeite` ohne das Prop bekommt das bisherige, unmittelbare
 * Ladeverhalten) und laeuft GENAU EINMAL: die erste Flanke `false -> true`. Ein spaeteres
 * Wegnavigieren und Zurueckkehren laedt nicht erneut nach - "Nicht die ganze Seite neu laden" gilt
 * sinngemaess auch fuer den ersten Ladevorgang selbst.
 *
 * ================= Terminart-Übersetzung: EINE Zuordnung =================
 *
 * `feld` ist im Backend bewusst freier Text (weitere Terminarten folgen) - `TERMINART_LABEL` ist die
 * eine Stelle, die zwischen Rohwert und Klartext übersetzt; eine unbekannte Terminart zeigt den
 * Rohwert, statt zu verschwinden.
 *
 * ================= Absprung =================
 *
 * `onOeffnen(bestellungId, art)` ist derselbe Weg wie in `Handlungsbedarf.tsx`/`Historie.tsx`
 * (`UebersichtSeite.tsx` reicht ihn unverändert durch). `art` geht als `"shipment"` hinaus - ein Wert,
 * den `App.tsx#uebersichtOeffnen` (noch) nicht besonders behandelt und der deshalb wie jeder unbekannte
 * Wert auf den Standardreiter "main" fällt (siehe dortiger Kopfkommentar) - dieselbe Behandlung, die
 * `Historie.tsx` für `kind === "status"` bereits in Kauf nimmt.
 *
 * ================= Bewusst WEGGELASSEN: ein Bestätigen-Knopf je Zeile =================
 *
 * Der Auftragstext erlaubt ihn ausdrücklich nur, "wenn es sich ohne Gedränge einfügt" - bei vier
 * Datenspalten je Zeile (Terminart, Alt, Pfeil, Neu; seit dem Verschiebungsbalken sechs, mit Balken
 * und Zahl) wäre ein weiteres Bedienelement je Zeile genau das Gedränge, das der Satz meint. Der
 * Gruppen-Knopf ("Zur Kenntnis genommen") ist der vom Auftraggeber genannte Bezug und bleibt der
 * einzige.
 *
 * ================= Zeilen aus `termine`, Bestätigen über `aenderungen` (Verschiebungsbalken) =========
 *
 * Plan `docs/superpowers/plans/2026-10-18-terminaenderungen-verschiebungsbalken.md`, Task 3. Gezeichnet
 * wird je Sendung eine Zeile je Terminart aus `gruppe.termine` (Backend fasst die Rohzeilen zusammen:
 * eine DSV-Änderung erzeugt je verknüpfter Bestellung eine Rohzeile, soll aber EINE Zeile ergeben).
 * Die Rechnung (Tage, Stufe, Zahlentext, Balkengeometrie, Sortierung) steht in
 * `lib/terminVerschiebung.ts`; hier wird nur gezeichnet.
 *
 * "Zur Kenntnis genommen" schickt dagegen weiter ALLE `aenderungen[].id` der Sendung - auch die
 * Mehrfachzeilen je Bestellung, die als Zeile gar nicht mehr sichtbar sind. Der Zähler im Kopf zählt
 * wie das Backend (`offenGesamt` = Sendung x Feld) und fällt deshalb um `gruppe.termine.length`, nicht
 * um die Zahl der Rohzeilen.
 *
 * Eine Balkenzeile trägt ihre Bedeutung als Text (`aria-label`, dazu die Zahl neben dem Balken) - die
 * Stufenfarbe allein darf nichts mitteilen.
 *
 * ================= Kontotyp "Nur lesen" (Task 6 des Plans „Kontotyp Nur lesen") =================
 *
 * "Zur Kenntnis genommen" schreibt (`POST /api/dsv/terminaenderungen/bestaetigen`) und wird vom Server fuer
 * ein Konto mit nur `view_*`-Rechten abgelehnt. Mit `nurLeser` (`state/nurLeser.ts`) entfaellt der Knopf
 * ganz - eine reine Aktion, kein Eingabefeld, deshalb ausgeblendet statt gesperrt. Die Liste, die Zeilen und
 * der Absprung zur Bestellung (Lesen) bleiben.
 *
 * ================= Lieferant(en) in der Sendungs-Kopfzeile (Auftrag "Übersichtsseite - Untertitel,
 * Bestellbezug, Lieferant bei Terminänderungen, Breiten-Check", 06.09.2026) =================
 *
 * Eine Sendung kann an mehreren Bestellungen haengen (`order_shipments` ist n:m, siehe
 * `backend/src/http/routes/terminaenderungen.ts:118-121`), jede Bestellung hat in JTL genau einen
 * Lieferanten. NORMALERWEISE ist das fuer alle Bestellungen einer Sendung derselbe - der Auftraggeber
 * will den Fall mehrerer UNTERSCHIEDLICHER trotzdem abgedeckt sehen. `lieferantenNamen(gruppe)`
 * sammelt die EINDEUTIGEN, nicht-leeren Lieferantennamen ueber `new Set(...)`: ein einzelner Name
 * erscheint einmal als Text, mehrere als kleine Pillen (analog zu `ops-termine-bestellungen`s
 * Pillen, nur ohne Klickziel - der Lieferant selbst ist kein Absprungpunkt). Kein ermittelbarer Name
 * (alle Bestellungen `lieferant: null`) zeigt NICHTS an, kein Platzhaltertext, kein Fehler -
 * dieselbe Zurueckhaltung wie beim Backend-Rueckfall selbst (siehe `TerminaenderungBestellung`s
 * Kopfkommentar in `api/terminaenderungen.ts`).
 */
import { useEffect, useRef, useState } from "react";
import { fmtDate } from "../../lib/format.ts";
import { FEHLERTEXTE, fehlertext, NutzerFehler } from "../../lib/fehlertext.ts";
import {
  terminaenderungenBestaetigen,
  terminaenderungenLaden,
  type SendungMitTerminaenderungen,
  type TerminaenderungenAntwort,
  type TerminaenderungBestellung,
  type TerminZeile,
} from "../../api/terminaenderungen.ts";
import {
  balkenGeometrie,
  mitVorzeichen,
  sendungenSortieren,
  verschiebungVon,
  zahlText,
  type Verschiebung,
} from "../../lib/terminVerschiebung.ts";
import type { Order } from "../../lib/types.ts";
import { darfBestellungOeffnen } from "../Drawer/Drawer.tsx";
import { Ikone } from "../Ikone.tsx";
import { Ladefehler } from "../Ladefehler/Ladefehler.tsx";
import { BestellungSplitMarke } from "../SplitMarke.tsx";

/** Die eine Stelle, an der Rohfeld auf Klartext trifft - siehe Kopfkommentar. */
const TERMINART_LABEL: Record<string, string> = {
  eta: "Ankunft (ETA)",
  etd: "Abfahrt (ETD)",
};

function terminartLabel(feld: string): string {
  return TERMINART_LABEL[feld] ?? feld;
}

/**
 * Die anzuzeigende Kennung einer verknuepften Bestellung, Auftrag "be-*-Bestellnummer statt
 * jtl_order_id in Terminaenderungen-Pills" (01.09.2026): `orderNo` (die volle JTL-Bestellnummer,
 * z. B. "BE-10281") bevorzugt, `bestellnummer` (die rohe `jtl_order_id`) als Rueckfall - dasselbe
 * Fallback-Muster wie `lib/bestellkennung.ts#kennungVon` (dort `orderNo ?? id`).
 */
function bestellungAnzeige(bestellung: TerminaenderungBestellung): string {
  return bestellung.orderNo ?? bestellung.bestellnummer;
}

/**
 * Die eindeutigen, nicht-leeren Lieferantennamen aller mit der Sendung verknuepften Bestellungen -
 * siehe Kopfkommentar "Lieferant(en) in der Sendungs-Kopfzeile". `new Set(...)` verwirft doppelte
 * Namen (der haeufige Fall: alle Bestellungen derselben Sendung haben denselben Lieferanten), ein
 * leeres Ergebnis bedeutet "kein Name ermittelbar" - der Aufrufer zeigt dann nichts an.
 */
function lieferantenNamen(gruppe: SendungMitTerminaenderungen): string[] {
  return [...new Set(gruppe.bestellungen.map((b) => b.lieferant).filter((l): l is string => !!l))];
}

/**
 * Die beiden Datumsspalten einer Terminzeile (Plan, "Darstellung je `TerminZeile`"): `alt` ist bei
 * einer Verschiebung der Stand der letzten Kenntnisnahme (`bekannt`) - der Ursprung steht klein
 * darunter, siehe `TerminReihe` -, bei `erstbefuellung` "war unbekannt", bei `zugeordnet`
 * "zugeordnet", sonst `bekannt` oder "war unbekannt". `neu` ist der heutige Termin oder, wenn er
 * entfallen ist, "Termin entfallen".
 */
function datumsSpalten(termin: TerminZeile): { alt: string; neu: string } {
  const neu = termin.aktuell ? fmtDate(termin.aktuell) : "Termin entfallen";
  if (termin.art === "erstbefuellung") return { alt: "war unbekannt", neu };
  if (termin.art === "zugeordnet") return { alt: "zugeordnet", neu };
  return { alt: termin.bekannt ? fmtDate(termin.bekannt) : "war unbekannt", neu };
}

/**
 * Text fuer Screenreader zu einer Balkenzeile, z. B. "Ankunft (ETA) +12 Tage, davon +5 neu" - die
 * Bedeutung des Balkens soll nie nur ueber seine Farbe laufen. `haupt` kommt unveraendert aus `zahlText`
 * ("+12"; bei "11 Tage früher" steht die Einheit schon darin). Ob es ein "davon" gibt, entscheidet
 * `zusatz` (`null` = nichts war schon bekannt); die Zahl dahinter wird aus `v.neu` formatiert, nicht aus
 * dem Anzeigetext des Zusatzes herausgeschnitten.
 */
function balkenBeschreibung(label: string, v: Verschiebung, haupt: string, zusatz: string | null): string {
  const dauer = v.gesamt < 0 ? haupt : `${haupt} ${v.gesamt === 1 ? "Tag" : "Tage"}`;
  const davon = zusatz === null ? "" : `, davon ${mitVorzeichen(v.neu)} neu`;
  return `${label} ${dauer}${davon}`;
}

/**
 * Eine Zeile des Panels: `feld | alt | -> | neu | balken | zahl`. Nur eine `verschiebung` hat Balken und
 * Zahl (`verschiebungVon` ist sonst `null`), die anderen Arten bleiben vierspaltig.
 */
function TerminReihe({ termin }: { termin: TerminZeile }) {
  const label = terminartLabel(termin.feld);
  const { alt, neu } = datumsSpalten(termin);
  const verschiebung = verschiebungVon(termin);
  const zeigeUrsprung = verschiebung !== null && termin.ursprung !== null && termin.ursprung !== termin.bekannt;

  const kopf = (
    <>
      <span className="ops-termine-feld">{label}</span>
      <span className="ops-termine-alt">
        {alt}
        {zeigeUrsprung ? (
          <span className="ops-termine-ursprung">ursprünglich {fmtDate(termin.ursprung)}</span>
        ) : null}
      </span>
      <span className="ops-termine-pfeil">→</span>
      <span className="ops-termine-neu">{neu}</span>
    </>
  );

  if (verschiebung === null) return <div className="ops-termine-zeile">{kopf}</div>;

  const geometrie = balkenGeometrie(verschiebung);
  const { haupt, zusatz } = zahlText(verschiebung);
  const abschnitt = (a: { links: number; breite: number }) => ({ left: `${a.links}%`, width: `${a.breite}%` });
  return (
    <div
      className="ops-termine-zeile"
      role="group"
      aria-label={balkenBeschreibung(label, verschiebung, haupt, zusatz)}
    >
      {kopf}
      <span className={`ops-termine-balken ops-termine-balken--${verschiebung.stufe}`} aria-hidden="true">
        <span className="ops-termine-achse" />
        {geometrie.blass ? (
          <span
            className="ops-termine-abschnitt ops-termine-abschnitt--blass"
            style={abschnitt(geometrie.blass)}
          />
        ) : null}
        {geometrie.kraeftig ? (
          <span
            className={`ops-termine-abschnitt ops-termine-abschnitt--kraeftig${
              geometrie.ruecklaeufig ? " ops-termine-abschnitt--ruecklaeufig" : ""
            }`}
            style={abschnitt(geometrie.kraeftig)}
          />
        ) : null}
        {geometrie.gekappt ? (
          <span className={`ops-termine-kappung ops-termine-kappung--${geometrie.gekappt}`} />
        ) : null}
      </span>
      <span className={`ops-termine-zahl ops-termine-zahl--${verschiebung.stufe}`}>
        <span className="ops-termine-haupt">{haupt}</span>
        {zusatz !== null ? (
          <span className={`ops-termine-zusatz${verschiebung.neu < 0 ? " ops-termine-zusatz--frueher" : ""}`}>
            {zusatz}
          </span>
        ) : null}
      </span>
    </div>
  );
}

export interface TerminaenderungenProps {
  /** `openOverviewAction(id, kind)`-Weg, siehe Kopfkommentar. Der dritte, optionale Parameter
   * (Befund 1 der Pruefrunde 4, `App.tsx#uebersichtOeffnen`) reicht `bestellungAnzeige(bestellung)`
   * durch: die id stammt aus dem DSV-Backend, nicht aus dem Store, und faellt im Store
   * haeufiger einmal nicht (mehr) auf - dann soll die "nicht gefunden"-Meldung die lesbare
   * Kennung zeigen statt sie wegzuwerfen, obwohl sie hier vorlag. Seit Auftrag
   * "be-*-Bestellnummer statt jtl_order_id in Terminaenderungen-Pills" (01.09.2026) ist das
   * bevorzugt die ECHTE Bestellnummer (`orderNo`, `cEigeneBestellnummer`, z. B. "BE-10281"), nur
   * ohne sie faellt der Hinweis auf `orders.jtl_order_id` als Text zurueck
   * (`backend/src/http/routes/terminaenderungen.ts`). */
  onOeffnen: (bestellungId: string, art: string, kennungHinweis?: string) => void;
  /** Der aktuelle Storebestand; bekannte, in JTL geloeschte Bestellungen erhalten keinen Absprung. */
  bestellungen?: readonly Order[];
  /**
   * Ob die Übersicht die aktive Seite ist - geladen wird erst an der ersten Flanke `false -> true`,
   * siehe Kopfkommentar. Default `true`: ein direkt gerenderter Test dieser Komponente (oder von
   * `UebersichtSeite` ohne dieses Prop) laedt weiterhin sofort.
   */
  istAktiveSeite?: boolean;
  /** Kontotyp "Nur lesen": kein "Zur Kenntnis genommen". Ohne Angabe: vorhanden. */
  nurLeser?: boolean;
}

export function Terminaenderungen({
  onOeffnen,
  bestellungen,
  istAktiveSeite = true,
  nurLeser = false,
}: TerminaenderungenProps) {
  const [sendungen, setSendungen] = useState<SendungMitTerminaenderungen[]>([]);
  const [offenGesamt, setOffenGesamt] = useState(0);
  // Nur fuer die Sichtbarkeit VOR der ersten Antwort (kein `ops-empty` waehrend die echten Daten noch
  // unterwegs sind). Ein Ladefehler hat seine eigene Flaeche (`ladefehler` unten), siehe Kopfkommentar.
  const [laedt, setLaedt] = useState(true);
  const [gruppenFehler, setGruppenFehler] = useState<Record<string, string>>({});
  const [wirdBestaetigt, setWirdBestaetigt] = useState<Record<string, boolean>>({});
  const [grenze, setGrenze] = useState(6);
  // Verhindert ein zweites Laden bei einem spaeteren Wegnavigieren/Zurueckkehren - siehe Kopfkommentar.
  const bereitsGeladen = useRef(false);
  // Ladefehler (Fehleranzeige Paket 2, Regel L1): der Fehler selbst, eingewickelt, weil auch `undefined` ein
  // geworfener Wert sein kann. `null` heisst: kein Fehler.
  const [ladefehler, setLadefehler] = useState<{ ursache: unknown } | null>(null);
  // Der laufende Abruf - erstes Laden UND „Erneut laden" teilen ihn: ein neuer Lauf bricht den vorigen ab.
  const lauf = useRef<AbortController | null>(null);

  /**
   * Laedt die unbestaetigten Terminaenderungen. Liefert ein Promise, das IMMER erfuellt wird (`<Ladefehler>` faengt
   * kein abgelehntes Promise), und das erst nach dem Laden: die Flaeche bleibt waehrend des Neuversuchs im Baum,
   * ihr Knopf ist bis dahin gesperrt. Ein abgebrochener oder ueberholter Lauf schreibt nichts (L5).
   *
   * `await` in einem `try` statt `.then()/.catch()` direkt an `terminaenderungenLaden(...)`: ein synchron
   * werfender Aufrufer (in Tests, siehe Kopfkommentar, `verdrahtungAnlage.test.tsx`s eigene Anfragen-Attrappe)
   * wuerde sonst ungefangen aus dem Effekt heraus- und die ganze Seite ohne Fehlergrenze abstuerzen lassen -
   * `await` faengt BEIDE Faelle (Wurf und Ablehnung) gleich.
   */
  async function terminaenderungenHolen(): Promise<void> {
    lauf.current?.abort();
    const abbruch = new AbortController();
    lauf.current = abbruch;
    let ergebnis: { antwort: TerminaenderungenAntwort } | { ursache: unknown };
    try {
      const antwort = await terminaenderungenLaden(abbruch.signal);
      // Eine Antwortform, die nicht zur Schnittstelle passt (siehe Kopfkommentar, Kollision mit fremden
      // `fetch`-Attrappen), ist ein Ladefehler und KEINE leere Liste: „Keine offenen Terminänderungen." und
      // Zaehler 0 waeren dann eine Behauptung ueber einen Stand, den der Hub nie gesehen hat.
      ergebnis = Array.isArray(antwort?.sendungen)
        ? { antwort }
        : { ursache: new NutzerFehler(FEHLERTEXTE.antwortUnvollstaendigLesen) };
    } catch (ursache) {
      ergebnis = { ursache };
    }
    if (abbruch.signal.aborted) return; // eigener Abbruch (Unmount) oder ueberholter Lauf, kein echter Fehlschlag.
    if ("ursache" in ergebnis) {
      setLadefehler({ ursache: ergebnis.ursache });
    } else {
      setSendungen(ergebnis.antwort.sendungen);
      setOffenGesamt(ergebnis.antwort.offenGesamt ?? 0);
      setLadefehler(null);
    }
    setLaedt(false);
  }

  // Das ERSTE Laden: genau einmal, an der ersten Flanke `false -> true`. Der Abruf bleibt stehen, wenn die Seite
  // wieder inaktiv wird - ihn dort abzubrechen liesse den Kasten fuer immer leer (`bereitsGeladen` bleibt `true`,
  // ein zweites Laden gibt es nicht).
  useEffect(() => {
    if (!istAktiveSeite || bereitsGeladen.current) return;
    bereitsGeladen.current = true;
    void terminaenderungenHolen();
  }, [istAktiveSeite]);

  // Abbau beim Unmount: den laufenden Abruf abbrechen. `bereitsGeladen` wird zurueckgesetzt, damit ein erneutes
  // Einhaengen (StrictMode simuliert Aus- und Einhaengen) den Abruf neu startet, statt ihn verloren zu geben.
  useEffect(
    () => () => {
      lauf.current?.abort();
      bereitsGeladen.current = false;
    },
    [],
  );

  async function gruppeBestaetigen(gruppe: SendungMitTerminaenderungen): Promise<void> {
    const ids = gruppe.aenderungen.map((a) => a.id);
    setWirdBestaetigt((vorher) => ({ ...vorher, [gruppe.shipmentId]: true }));
    try {
      // `await` statt `.then()/.catch()` direkt am Aufruf - derselbe Grund wie beim initialen Laden
      // oben: faengt auch einen synchron werfenden Aufrufer.
      await terminaenderungenBestaetigen(ids);
      setSendungen((vorher) => vorher.filter((s) => s.shipmentId !== gruppe.shipmentId));
      // Der Zaehler zaehlt Sendung x Feld (= `termine`), nicht die Rohzeilen in `ids` - siehe Kopfkommentar.
      setOffenGesamt((vorher) => Math.max(0, vorher - gruppe.termine.length));
      setGruppenFehler((vorher) => {
        if (!(gruppe.shipmentId in vorher)) return vorher;
        const { [gruppe.shipmentId]: _entfernt, ...rest } = vorher;
        return rest;
      });
    } catch (fehler) {
      console.error(`Terminaenderungen zu "${gruppe.shipmentId}" konnten nicht bestaetigt werden:`, fehler);
      setGruppenFehler((vorher) => ({
        ...vorher,
        // Die Zeile an der Gruppe gab es schon (Spec R2): neu ist die Ursache dahinter. `null` (Abbruch) laesst
        // die Zeile leer und damit weg.
        [gruppe.shipmentId]: fehlertext(fehler, "Bestätigen fehlgeschlagen.") ?? "",
      }));
    } finally {
      setWirdBestaetigt((vorher) => {
        const { [gruppe.shipmentId]: _entfernt, ...rest } = vorher;
        return rest;
      });
    }
  }

  // Schwerste Verschiebung zuerst, DANN abschneiden - sonst fehlte die schlimmste Sendung, wenn sie spaet kommt.
  const sichtbareGruppen = sendungenSortieren(sendungen).slice(0, grenze);
  const darfDsvBestellungOeffnen = (bestellungId: string): boolean => {
    const bestellungImStore = bestellungen?.find((eintrag) => String(eintrag.id) === bestellungId);
    return bestellungen === undefined || darfBestellungOeffnen(bestellungImStore);
  };

  return (
    <section className="ops-panel ops-termine-panel">
      <div className="ops-panel-head">
        <div className="ops-panel-title">
          <h2>
            {/* Bei einem Ladefehler ohne Zaehler: ein „0" behauptete, es gebe keine offenen Aenderungen. */}
            Terminänderungen {ladefehler === null ? <span className="ops-count">{offenGesamt}</span> : null}
          </h2>
          <p>Abweichungen zu bereits bekannten Sendungsterminen - je Sendung gruppiert</p>
        </div>
      </div>
      <div className="ops-actions">
        {ladefehler !== null ? (
          <Ladefehler
            was="Die Terminänderungen konnten nicht geladen werden."
            ursache={ladefehler.ursache}
            onErneutLaden={terminaenderungenHolen}
          />
        ) : sichtbareGruppen.length ? (
          sichtbareGruppen.map((gruppe) => {
            const lieferanten = lieferantenNamen(gruppe);
            return (
              <div className="ops-termine-gruppe" key={gruppe.shipmentId}>
                <div className="ops-termine-kopf">
                  <strong className="ops-termine-sendung">{gruppe.dsvShipmentNo}</strong>
                  {lieferanten.length === 1 ? (
                    <span className="ops-termine-lieferant">{lieferanten[0]}</span>
                  ) : lieferanten.length > 1 ? (
                    <span className="ops-termine-lieferanten">
                      {lieferanten.map((name) => (
                        <span key={name} className="ops-termine-lieferant">
                          {name}
                        </span>
                      ))}
                    </span>
                  ) : null}
                  <span className="ops-termine-bestellungen">
                    {gruppe.bestellungen.length ? (
                      gruppe.bestellungen.map((bestellung) =>
                        darfDsvBestellungOeffnen(bestellung.orderId) ? (
                          <span key={bestellung.orderId} className="ops-termine-paar">
                            <button
                              type="button"
                              className="ops-termine-link"
                              onClick={() => onOeffnen(bestellung.orderId, "shipment", bestellungAnzeige(bestellung))}
                            >
                              {bestellungAnzeige(bestellung)}
                              <span className="ops-arrow">
                                <Ikone name="arrow-right" />
                              </span>
                            </button>
                            {/* Geschwister des Knopfes, nicht darin: ein Knopf im Knopf waere ungueltig. */}
                            <BestellungSplitMarke orderId={bestellung.orderId} />
                          </span>
                        ) : (
                          <span key={bestellung.orderId} className="ops-termine-link">
                            {bestellungAnzeige(bestellung)}
                            <BestellungSplitMarke orderId={bestellung.orderId} />
                          </span>
                        ),
                      )
                    ) : (
                      <span className="ops-termine-ohne-bestellung">keiner Bestellung zugeordnet</span>
                    )}
                  </span>
                  {nurLeser ? null : (
                    <button
                      type="button"
                      className="ops-termine-bestaetigen"
                      disabled={!!wirdBestaetigt[gruppe.shipmentId]}
                      onClick={() => gruppeBestaetigen(gruppe)}
                    >
                      Zur Kenntnis genommen
                    </button>
                  )}
                </div>
                <div className="ops-termine-zeilen">
                  {gruppe.termine.map((termin) => (
                    <TerminReihe termin={termin} key={termin.feld} />
                  ))}
                </div>
                {gruppenFehler[gruppe.shipmentId] ? (
                  <div className="ops-termine-fehler">{gruppenFehler[gruppe.shipmentId]}</div>
                ) : null}
              </div>
            );
          })
        ) : laedt ? null : (
          <div className="ops-empty">Keine offenen Terminänderungen.</div>
        )}
      </div>
      {sendungen.length > 6 ? (
        <button className="ops-panel-more" onClick={() => setGrenze((alt) => (alt === 6 ? Infinity : 6))}>
          {grenze === 6 ? `${sendungen.length - 6} weitere anzeigen` : "Auf die wichtigsten 6 reduzieren"}
        </button>
      ) : (
        ""
      )}
    </section>
  );
}
